<script setup lang="ts">
import { computed, onMounted } from "vue";

import { useDashboardMonoController } from "@/app/data/dashboard/dashboard-mono.controller";
import { EDashboardType } from "@/app/data/dashboard/dashboard.types";
import { useDashboardDateRange } from "@/composables/dashboard/use-dashboard-date-range";
import { useDashboardMonositeQueries } from "@/composables/dashboard/use-dashboard-monosite-queries";
import { DATA_TEST_IDS } from "@/constants/data-test-ids/data-test-ids.constant";
import { TCommonConsumption } from "@/data/api/kili/consumption/types/common-consumption.type";
import { mapCommonConsumption } from "@/data/api/v4b/site-consumption/legacy-consumption.mapping";
import { V4HSiteId } from "@/data/common/ids";
import { keyOf, useTypedIntl } from "@/locales/typed-intl";
import { EMixpanelEvent } from "@/services/mixpanel/mixpanel-events.type";
import { MixpanelService } from "@/services/mixpanel/mixpanel-service";

import DeviceConsumptionDistributionChart from "@/app/data/dashboard/cards/common/device-consumption-distribution-chart.vue";
import PodiumCard from "@/app/data/dashboard/cards/common/podium-card.vue";
import AreaConsumptionChartMono from "@/app/data/dashboard/cards/mono/charts/area-consumption-chart-mono.vue";
import ConsumptionComparisonChartMono from "@/app/data/dashboard/cards/mono/charts/consumption-comparison-chart-mono.vue";
import ConsumptionEvolutionChartMono from "@/app/data/dashboard/cards/mono/charts/consumption-evolution-chart-mono.vue";
import YearlyConsumptionHeatmapMono from "@/app/data/dashboard/cards/mono/charts/yearly-consumption-heatmap-mono.vue";
import InsightsMono from "@/app/data/dashboard/cards/mono/insights-mono.vue";
import TopFlopConsumptionCardsMono from "@/app/data/dashboard/cards/mono/top-flop-consumption-cards-mono.vue";
import MissingDsoSubscriptionDialog from "@/app/data/dashboard/dialog/missing-dso-subscription-dialog.vue";
import MissingSectionSurfaceAreaMonoDialog from "@/app/data/dashboard/dialog/missing-section-surface-area-mono-dialog.vue";
import ContactVoltalisButton from "@/components/contact/contact-voltalis-button.vue";

interface IProps {
  fromDay: Date;
  toDay: Date;
  siteId: V4HSiteId;
}

const props = defineProps<IProps>();

const { t, k } = useTypedIntl();

const { dateRange } = useDashboardDateRange();

const siteId = computed<V4HSiteId>(() => props.siteId);

const { siteConsumption, missingInfos, isSiteConsumptionPending } = useDashboardMonositeQueries(siteId, dateRange);

const {
  isMissingDsoSubscriptionDialogVisible,
  isMissingSectionSurfaceAreaDialogVisible,
  podiumItems,
  isMissingAllSectionSurfaceArea,
} = useDashboardMonoController(siteConsumption, missingInfos, undefined);

const deviceConsumptionDistribution = computed<TCommonConsumption | undefined>(() => {
  if (!siteConsumption.value) return;

  return mapCommonConsumption(siteConsumption.value);
});

onMounted(() => {
  MixpanelService.trackEvent(EMixpanelEvent.DASHBOARD_MONO);
});
</script>

<template>
  <div
    class="flex flex-col gap-6 pb-8"
    :data-test-id="DATA_TEST_IDS.views.dashboard.monosite"
  >
    <div class="2xl:grid grid-flow-col grid-cols-3 max-2xl:flex max-2xl:flex-col gap-6">
      <InsightsMono
        class="col-span-2"
        :is-site-consumption-pending="isSiteConsumptionPending"
        :missing-infos="missingInfos"
        :site-consumption="siteConsumption"
        :site-id="siteId"
        @open-missing-subscription-dialog="isMissingDsoSubscriptionDialogVisible = true"
      />

      <PodiumCard
        :data-test-id="DATA_TEST_IDS.views.dashboard.podiums.lessEnergyConsummingPodium"
        :is-loading="isSiteConsumptionPending"
        :items="podiumItems"
        :type="EDashboardType.MONOSITE"
      />
    </div>

    <div class="grid grid-flow-row xl:grid-cols-2 max-xl:grid-cols-1 gap-6">
      <ConsumptionEvolutionChartMono
        :from-day="props.fromDay"
        :site-id="siteId"
        :to-day="props.toDay"
      />

      <AreaConsumptionChartMono
        :from-day="props.fromDay"
        :site-id="siteId"
        :to-day="props.toDay"
      />
    </div>

    <div class="grid grid-flow-row xl:grid-cols-5 max-xl:grid-cols-1 gap-6">
      <ConsumptionComparisonChartMono
        class="xl:col-span-3"
        :from-day="props.fromDay"
        :site-id="siteId"
        :to-day="props.toDay"
      />

      <DeviceConsumptionDistributionChart
        class="xl:col-span-2"
        :consumption="deviceConsumptionDistribution"
        :data-test-id="DATA_TEST_IDS.views.dashboard.charts.consumptionDistributionPerDeviceTypesChart"
        :has-no-data="!siteConsumption?.devicesCount"
        :is-loading="isSiteConsumptionPending"
        :type="EDashboardType.MONOSITE"
      >
        <template #noDataMessage>
          <div class="flex flex-col gap-4">
            <h5>{{ t(k.data.dashboard.noDataChartMessage.title) }}</h5>
            <i18n-t
              :keypath="keyOf(k.data.dashboard.noDataChartMessage.noInstalledDevices)"
              tag="p"
            >
              <template #installedDevices>
                <b>
                  {{ t(k.data.dashboard.noDataChartMessage.installedDevices) }}
                </b>
              </template>
            </i18n-t>

            <ContactVoltalisButton :data-test-id="DATA_TEST_IDS.views.dashboard.buttons.contactVoltalisBtn" />
          </div>
        </template>
      </DeviceConsumptionDistributionChart>
    </div>

    <TopFlopConsumptionCardsMono
      :is-loading="isSiteConsumptionPending"
      :sections-consumption="siteConsumption?.devicesBySection"
    />

    <YearlyConsumptionHeatmapMono :site-id="siteId" />
  </div>

  <MissingSectionSurfaceAreaMonoDialog
    v-model:visible="isMissingSectionSurfaceAreaDialogVisible"
    :no-data="isMissingAllSectionSurfaceArea"
    :site-id="siteId"
  />

  <MissingDsoSubscriptionDialog
    v-model:visible="isMissingDsoSubscriptionDialogVisible"
    :type="EDashboardType.MONOSITE"
  />
</template>
